import React, { useEffect, useState, useRef } from "react";
import { Box, Breakpoint, Button, CircularProgress, Divider, Icon, InputAdornment, Paper, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Formik, FormikErrors, FormikHelpers, FormikTouched } from "formik";
import * as yup from 'yup';
import { AppColor, AppConstants, Fonts } from "../../../../types/Constants";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { checkIdentifier, createExperience, deleteQRIcon, deleteSettingsAsset, fetchError, getMyBusinessGroups, setCurrentBusinessGroup, showMessage, updateExperience, uploadQRIcon, uploadSettingsAsset } from "../../../../redux/actions";
import { ArrowDownward } from "@mui/icons-material";
import { Category } from "../../../../types/models/Category";
import CategoriesList from "../categories/CategoriesList";
import CategoriesMenu from "../categories/CategoriesMenu";
import SettingsPage from "../settings/SettingsPage";
import { Settings } from "../../../../types/models/Settings";
import { clearCategories, getCategories, uploadCategoryImage } from "../../../../redux/actions/Category";
import { useParams } from "react-router-dom";
import QRCodeGen from "../settings/QRCodeGen";
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import { convertByteString, getARPath } from "../../../../utils/utils";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";

export const BeaconEditorModes = ["CREATE", "EDIT", "MOD_ONLY", "READ_ONLY"] as const;

interface ExperienceEditorProps {
    maxWidth?: Breakpoint;
    isOpen: boolean;
    mode?: typeof BeaconEditorModes[number];
    onSubmitted: (group_id?: string) => void;
    onClose: () => void;
    cancelSettingsForm: () => void;
    handleDeleteGroup: () => void;
    refreshFlag: boolean;
    setRefreshFlag: (flag: boolean) => void;
    heightOffset: number;
}

const ExperienceEditor: React.FC<ExperienceEditorProps> = (prop) => {
    // Util
    let { group_id } = useParams();
    const themeApp = useTheme();
    const fullscreen = useMediaQuery(themeApp.breakpoints.down('md'));
    const dispatch = useAppDispatch();

    const businessGroupState = useAppSelector(state => state.businessGroup);
    const categoryGroupState = useAppSelector(state => state.category);

    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [currentFilterFile, setCurrentFilterFile] = useState<File | null>(null);
    const [filterError, setFilterError] = useState<string>("");

    const [currentAssetFile, setCurrentAssetFile] = useState<File | undefined>(undefined);
    const [deleteAssetImage, setDeleteAssetImage] = useState(false);

    const [openFileSizeExceededMessage, setOpenFileSizeExceededMessage] = useState<boolean>(false);

    const [scrollY, setScrollY] = useState(0);

    const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const scrollToSection = (index: number) => {
        if (containerRef.current && sectionRefs.current[index]) {

            const section = sectionRefs.current[index];
            const offsetTop = section ? section.offsetTop : 0;

            containerRef.current.scrollTo({
            top: offsetTop - prop.heightOffset,
            behavior: "smooth",
            });
        }
      };
    
    const handleScroll = () => { setScrollY(containerRef.current?.scrollTop ?? 0); }; 

    useEffect(() => {
      containerRef.current?.addEventListener('scroll', handleScroll);
      return () => { containerRef.current?.removeEventListener('scroll', handleScroll); };

    }, []);

    useEffect(() => {
        getCategoryData();
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
    }, [businessGroupState.currentBusinessGroup]);

    useEffect(() => {
        if (prop.refreshFlag === true) {
          getCategoryData();
          prop.setRefreshFlag(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prop.refreshFlag]);

    const validationSchema = yup.object({
        name: yup.string().required("Please enter an Experience name").max(40, "Experience name must be at most 40 characters"),
        categories: yup
          .array()
          .test(
            "name-test",
            "Please enter Category name",
            (categories) => {
              if (!categories) return false;
              return categories.every(
                (cat) => typeof cat.Name === "string" && cat.Name.trim() !== ""
              );
            }
          ),
      });

      const initialValues: Settings = {
        name: businessGroupState.currentBusinessGroup?.name ?? "",
        identifier: businessGroupState.currentBusinessGroup?.subdomain ?? "",
        categories: categoryGroupState.categories ?? [],
        title: businessGroupState.currentBusinessGroup?.title ?? "",
        description: businessGroupState.currentBusinessGroup?.description ?? "",
        color: businessGroupState.currentBusinessGroup?.color ?? "#D0CCCC",
        qr_fgcolor: "#" + (businessGroupState.currentBusinessGroup?.qr_fgcolor ?? "0C2340"),
        qr_bgcolor: "#" + (businessGroupState.currentBusinessGroup?.qr_bgcolor ?? "ffffff"),
        theme: businessGroupState.currentBusinessGroup?.theme ?? "ball",
        qrIcon: undefined,
        has_custom_asset: businessGroupState.currentBusinessGroup?.has_custom_asset ?? false,
        beacon_group_id: businessGroupState.currentBusinessGroup?.id ?? null,
        last_modified: businessGroupState.currentBusinessGroup?.last_modified ?? ""
      };


      const handleCategorySelect = (category: Category | null) =>  {

        setSelectedCategory(category);

    };

    const getCategoryData = () => {
        //Set the categories into state
        if (businessGroupState.currentBusinessGroup) {
          const group_id = businessGroupState.currentBusinessGroup.id;
          dispatch(
            getCategories(group_id,(categories) => {
              if (!categories){
                dispatch(fetchError("Failed to retrieve categories"));
              }
          }));
        } else {
          dispatch(clearCategories());
        }
    };
    
    const callUpdateExperience = (settings: Settings, setSubmitting: (isSubmitting: boolean) => void) => {
        setIsLoading(true);

        dispatch(updateExperience(settings, async (result, categoryIds) => {
            if (result) {
                dispatch(showMessage("This Experience has been saved"));
                const groupid = businessGroupState.currentBusinessGroup?.id;

                const uploadPromises: Promise<any>[] = [];

                settings.categories?.forEach(category => {
                  if (category.currentFilterFile) {
                    const formData = new FormData();
                    formData.append('file', category.currentFilterFile, category.currentFilterFile.name);
                    
                    const matchingCategory = categoryIds?.find(cat => cat.index === category.Index);

                    let categoryId = category.Id ? category.Id : matchingCategory?.categoryId;
                    const uploadCateogryPromise = new Promise((resolve) => {
                      dispatch(uploadCategoryImage(groupid ?? "", categoryId ?? "", formData, "FILTER" ,(result) => {
                        if (result) {
                          setCurrentFilterFile(null);
                          setFilterError("");
                        }
                        else{
                          setFilterError("Error Uploading Icon");
                        }
                        resolve(result);
                      }));
                    });
                    uploadPromises.push(uploadCateogryPromise);
                  }
                });

                if (currentAssetFile) {
                  const formData = new FormData();
          
                  formData.append('file', currentAssetFile, currentAssetFile.name);
                  const uploadAssetPromise = new Promise((resolve) => {
                    dispatch(uploadSettingsAsset(groupid ?? "", formData,(result) => {
                      if (result) {
                        setCurrentAssetFile(undefined);
                      }
                      resolve(result);
                    }));
                  });
                  uploadPromises.push(uploadAssetPromise);
                }
                else if (deleteAssetImage) {
                  const deleteAssetPromise = new Promise((resolve) => {
                    dispatch(deleteSettingsAsset(groupid ?? "", (result) => {
                      if (result) {
                        setDeleteAssetImage(false);
                      }
                      resolve(result);
                    }));
                  });
                  uploadPromises.push(deleteAssetPromise);
                } 

                if (settings.qrIcon) {
                  const formData = new FormData();
                  formData.append('file', settings.qrIcon, settings.qrIcon.name);
                  const qrIconPromise = new Promise((resolve) => {
                    dispatch(uploadQRIcon(groupid ?? "", formData, (result) => {
                      resolve(result);
                    }));
                  });
                  uploadPromises.push(qrIconPromise);
                } else {
                  if (settings.qrIcon === null) {
                    const qrIconPromise = new Promise((resolve) => {
                      dispatch(deleteQRIcon(groupid ?? "", (result) => {
                        resolve(result);
                      }));
                    });
                    uploadPromises.push(qrIconPromise);
                  }
                }

                await Promise.all(uploadPromises);

                dispatch(
                    getMyBusinessGroups(({ businessGroups }) => {
                      if (businessGroups && group_id) {
                        // Change the selected group when the URL includes business group id
                        const currentGroup = businessGroups.find((group) => group.id === group_id) ?? null;
                        dispatch(setCurrentBusinessGroup(currentGroup));
                      }
                    })
                );
                prop.onSubmitted();
            }
            setSubmitting(false);
            setIsLoading(false);
          }
          ));
        };

        const callCreateExperience = (settings: Settings, setSubmitting: (isSubmitting: boolean) => void) => {
          setIsLoading(true);
  
          dispatch(createExperience(settings, (result, id, categoryIds) => {
              if (result) {
                  dispatch(showMessage("This Experience has been created"));
                  const new_group_id = id;

                  settings.categories?.forEach(category => {
                    if (category.currentFilterFile) {
                      const formData = new FormData();
                      formData.append('file', category.currentFilterFile, category.currentFilterFile.name);
                      
                      const matchingCategory = categoryIds?.find(cat => cat.index === category.Index);

                      let categoryId = category.Id ? category.Id : matchingCategory?.categoryId;

                      dispatch(uploadCategoryImage(new_group_id ?? "", categoryId ?? "", formData, "FILTER" ,(result) => {
                        if (result) {
                          setCurrentFilterFile(null);
                          setFilterError("");
                        }
                        else{
                          setFilterError("Error Uploading Icon");
                        }
                      }));
  
                    }
                  });

                  if (settings.qrIcon) {
                    const formData = new FormData();
                    formData.append('file', settings.qrIcon, settings.qrIcon.name);

                    dispatch(uploadQRIcon(new_group_id?? "", formData, (result) => {
                      if (result) {
                        console.log("Uploaded icon");
                      }
                    }));

                  }

                  if (currentAssetFile) {
                    const formData = new FormData();
            
                    formData.append('file', currentAssetFile, currentAssetFile.name);
            
                    dispatch(uploadSettingsAsset(new_group_id ?? "", formData,(result) => {
                      if (result) {
                        setCurrentAssetFile(undefined);
                        
                      }
                      setIsLoading(false);
                    }));
                  }
                  
                  else if (deleteAssetImage) {
                    dispatch(deleteSettingsAsset(new_group_id ?? "", (result) => {
                      if (result) {
                        setDeleteAssetImage(false);
                      }
                    }));
                  } 

                  dispatch(
                      getMyBusinessGroups(({ businessGroups }) => {
                        if (businessGroups && new_group_id) {
                          // Change the selected group when the URL includes business group id
                          const currentGroup = businessGroups.find((group) => group.id === new_group_id) ?? null;
                          dispatch(setCurrentBusinessGroup(currentGroup));
                        }
                      })
                    );
  
                  prop.onSubmitted(new_group_id ?? "");
              }
              setSubmitting(false);
              setIsLoading(false);
            }
          ));
        };  

    const handleSubmit = (values: Settings, formikHelpers: FormikHelpers<Settings>, ) => {
        const guid_length = 36;
        // Any categories with an index id, change to null 
        const updatedValues = {
          ...values,
          categories: values.categories?.map((category, index) => ({
              ...category,
              Id: (category.Id?.length ?? 0) < guid_length ? null : category.Id,
              Color: category.Color.replace("#", "")
          })) ?? [],
          qr_fgcolor: values.qr_fgcolor.replace("#", ""),
          qr_bgcolor: values.qr_bgcolor.replace("#", "")
        };

        if (prop.mode === "EDIT") {
            callUpdateExperience(updatedValues, formikHelpers.setSubmitting);
          } else {
            callCreateExperience(updatedValues, formikHelpers.setSubmitting);
          }
       
    };

    const InputForm = (
        values: Settings,
        errors: FormikErrors<Settings>,
        handleChange: (e: React.ChangeEvent<any>) => void,
        touched: FormikTouched<Settings>,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<Settings>>,
        setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void
      ) => {

        const [identifierStatus, setIdentifierStatus] = useState<boolean>(false);
        const [localIdentifierError, setLocalIdentifierError] = useState<boolean>(false);
        const [checking, setChecking] = useState(false);
        const debounceTime = 1000;

        useEffect(() => {
          if (!values.identifier || values.identifier.length == 0 || initialValues.identifier == values.identifier) {
            setIdentifierStatus(false);
            setLocalIdentifierError(false); 
            setChecking(false);
            return;
          }

          const isValid = /^[a-z0-9-]+$/.test(values.identifier);

          setChecking(true);

          if (!isValid) {
            setLocalIdentifierError(true);
            setChecking(false);
            return;
          }
          setLocalIdentifierError(false);
          
          const handler = setTimeout(() => {
            dispatch(checkIdentifier(values.identifier, (result) => {
              setIdentifierStatus(result);
              setChecking(false);
            }));
          }, debounceTime);
        
          return () => clearTimeout(handler);
        }, [values.identifier]);

        useEffect(() => {
          setChecking(false);
          setIdentifierStatus(false);
        }, [businessGroupState.currentBusinessGroup]);

        return (
            <div
            id="scrollable-container"
            style={{ overflowY: "auto", height: `calc(100vh - ${prop.heightOffset}px)` }}
            ref={containerRef}
            >
              <Box>
                <Paper id="section-1" ref={(el) => sectionRefs.current[0] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
                <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 1: Name</Typography>
                <Box sx={{ display: "flex", justifyContent:"space-between", alignItems: "end", p: 2 }}>
                <TextField
                  disabled={prop.mode === "MOD_ONLY" || prop.mode === "READ_ONLY"}
                  required
                  name="display_title"
                  value={values.name}
                  variant="outlined"
                  placeholder="..."
                  id="display_title"
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setFieldValue("name", newValue);
                  }}
                  error={((errors.name ? true : undefined) && touched.name)}
                  autoComplete="off"
                  aria-autocomplete="none"
                  helperText={errors.name && touched.name ? errors.name : `${values.name.length}/${40}`}
                  inputProps={{ maxLength: 40 }}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: 'right',
                    },
                  }}
                  sx={{ width: "300px" }}
                />
                  <Button onClick={() => scrollToSection(1)}>Next <ArrowDownward sx={{ ml: 4 }} /></Button>
                </Box>
                </Paper>
              </Box>
              <Box>
                <Paper id="section-2" ref={(el) => sectionRefs.current[1] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
                <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 2: ID</Typography>
                <Box sx={{ display: "flex", justifyContent:"space-between", alignItems: "center", p: 2 }}>
                  {/*  component here */}
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                  <Typography>{getARPath()}</Typography>
                  <TextField
                  disabled={prop.mode === "MOD_ONLY" || prop.mode === "READ_ONLY"}
                  required
                  name="display_title"
                  value={values.identifier}
                  variant="outlined"
                  size="small"
                  placeholder="..."
                  id="display_title"
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setFieldValue("identifier", newValue);
                  }}
                  error={identifierStatus || localIdentifierError}
                  autoComplete="off"
                  aria-autocomplete="none"
                  helperText={identifierStatus ? "Identifier unavailable" : (localIdentifierError ? "Only lowercase letters and numbers allowed" : `${values.identifier.length}/${40}`)}
                  inputProps={{ maxLength: 40 }}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: identifierStatus || localIdentifierError ? 'left' : 'right',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {checking ? (
                          <CircularProgress size={20} />
                        ) : values.identifier.length > 0 ? (
                            identifierStatus || localIdentifierError ? (
                              <ClearIcon sx={{ color: AppColor.redError }} />
                            ) : (
                              <CheckIcon sx={{ color: "green" }} />
                            )
                          ) : null
                        }
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: "300px" }}
                />
                </Box>
                <Button onClick={() => scrollToSection(2)}>Next <ArrowDownward sx={{ ml: 4 }} /></Button>
                </Box>
                </Paper>
              </Box>
              <Box>
                <Paper id="section-3" ref={(el) => sectionRefs.current[2] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4 }}>
                  <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 3: Categories</Typography>
                  <Box sx={{ display: "flex", justifyContent:"space-between", alignItems: "end" }}>
                    <Box sx={{ display: "flex", gap: 10 }}>
                        <CategoriesList
                            values={values}
                            touched={touched}
                            errors={errors} 
                            handleCategorySelect={handleCategorySelect}
                            setFieldValue={setFieldValue}
                            experienceMode={prop.mode ?? ""}
                        />
                        <CategoriesMenu
                        category={selectedCategory}
                        values={values}
                        touched={touched}
                        errors={errors}
                        setFieldValue={setFieldValue}
                        setOpenFileSizeExceededMessage={setOpenFileSizeExceededMessage}
                        experienceMode={prop.mode ?? ""}
                        />
                    </Box>
                    <Button onClick={() => scrollToSection(3)}>Next <ArrowDownward sx={{ ml: 4 }} /></Button>
                  </Box>
                </Paper>
              </Box>
              <Box>
              <Paper id="section-4" ref={(el) => sectionRefs.current[3] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4 }}>
              <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 4: QR Code</Typography>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Box sx={{ flex: 1, mt: 4 }}>
                    {/*  component here */}
                    <QRCodeGen
                        values={values}
                        setFieldValue={setFieldValue}
                        experienceMode={prop.mode ?? ""}
                    />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "end" }}>
              {/* <Button onClick={() => scrollToSection(4)}>Next <ArrowDownward sx={{ ml: 4 }} /></Button> */}
              </Box>
              </Paper>
              </Box>
              {/* <Box>
              <Paper id="section-5" ref={(el) => sectionRefs.current[4] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
              <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 5: Landing Page</Typography>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Box sx={{ flex: 1, mt: 4 }}>
                <SettingsPage
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    cancelSettingsForm={prop.cancelSettingsForm}
                    handleDeleteGroup={prop.handleDeleteGroup}
                    currentAssetFile={currentAssetFile}
                    setCurrentAssetFile={setCurrentAssetFile}
                    deleteAssetImage={deleteAssetImage}
                    setDeleteAssetImage={setDeleteAssetImage}
                />
                </Box>
                <Box sx={{ flex: 1, justifyContent: "center", alignItems: "flex-start", display: "flex" }}>
                  // Preview component here
                </Box>
              </Box>
              </Paper>
              </Box> */}
            </div>
        );
    };

    return (
        <Formik
        enableReinitialize
        validateOnChange={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, errors, touched, isSubmitting, handleChange, submitForm, resetForm, setFieldValue, setFieldTouched, setSubmitting }) => {
        return (
        <>
        <Box
            sx={{
            "& .MuiDialog-paper": {
                width: fullscreen ? "100%" : "70%",
                maxWidth: "1536px",
                backgroundColor: "white",
            },
            "& .MuiDialogContent-root": {
                overflowY: "auto",
                paddingLeft: 0,
                paddingRight: 0,
            },
            // minWidth: "800px",
            }}
        >
          <Box sx={{ 
            display: "flex", 
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 2
          }}>
            <Box sx={{ display: "flex", gap: 4, pb: 2, flexWrap: "wrap" }}>
              {/* <Typography>container ref: {scrollY}</Typography>
              <Typography>section ref: {sectionRefs.current[2]?.offsetTop}</Typography> */}
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY < 154 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(0)}
              >
                Step 1: Name
              </Button>
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY >= 154 && scrollY <= 302 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(1)}
              >
                Step 2: ID
              </Button>
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY > 302 && scrollY <= 1088 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(2)}
              >
                Step 3: Categories
              </Button>
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY > 1088 && scrollY <= 1576 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(3)}
              >
                Step 4: QR Code
              </Button>
              {/* <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY > 1576 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(4)}
              >
                Step 5: Landing Page
              </Button> */}
            </Box>
            <Box sx={{
              display: "flex", 
              flexWrap: "wrap",
              flexShrink: 0,
              alignSelf: "flex-end",
              paddingBottom: "16px",
            }}>
                {/* Close / Cancel - Mode-agnostic. */}
                <Button
                    variant="outlined"
                    sx={{ marginRight: 4 }}
                    onClick={prop.cancelSettingsForm}
                >
                    {prop.mode === "MOD_ONLY" ? "Close" : "Cancel"}
                </Button>
                {/* Save - General - Only visible outside of mod_only. */}
                {(prop.mode !== "MOD_ONLY" && prop.mode !== "READ_ONLY") && (
                    <Button
                    sx={{ marginRight: 2, flexShrink: 0 }}
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={submitForm}
                    >
                    Save
                    </Button>
                )}
                {/* Ban - Moderation - Requires MOD_ONLY
                {prop.mode === "MOD_ONLY" && (
                    <Button
                    sx={{ marginRight: 2 }}
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={() => {
                        setModerationAction("BAN");
                        setModerationConfirmDialogOpen(true);
                    }}
                    >
                    Ban User
                    </Button>
                )}
                */}
            </Box>
          </Box>
          <Divider />
            <Box style={{ paddingTop: 4  }}>
            {/* <AppScrollbar forceVisible="y" autoHide={false}> */}
                    {InputForm(
                    values,
                    errors,
                    handleChange,
                    touched,
                    setFieldValue,
                    setFieldTouched,
                    )}
            {/* </AppScrollbar> */}
            </Box>
        </Box>
        <AppConfirmDialog
          open={openFileSizeExceededMessage}
          onConfirm={() => setOpenFileSizeExceededMessage(false)}
          confirmButtonTitle="OK"
          disableDenyButton={true}
          title={`The image is too large. Images have a maximum filesize of ${convertByteString(AppConstants.MaxImageBytes)}. Please try resizing the image then uploading it again.`}
          dialogTitle="Problem attaching image"
          content=""
        />
        </>
        );
        }
        }
        </Formik >
    );

};
    
export default ExperienceEditor;